import { useState, useCallback, useRef } from 'react';
import { Dialog, DialogProps, Button, Box } from '@mui/material';

type UseDialogOptions = {
  modal?: boolean;
  dialogProps?: Omit<DialogProps, 'open' | 'onClose'>;
  showCloseButton?: boolean;
};

type UseDialogReturn = [
  React.FC<{ children: React.ReactNode }>,
  () => void,
  () => void,
  boolean
];

export const useDialog = (options?: UseDialogOptions): UseDialogReturn => {
  const { modal = true, dialogProps = {}, showCloseButton = true } = options || {};
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Ref stable pour éviter la recréation de DialogComponent à chaque changement de isOpen
  // (une recréation provoquerait un démontage/remontage des enfants et déclencherait leurs cleanups)
  const stateRef = useRef({ isOpen, close, modal, dialogProps, showCloseButton });
  stateRef.current = { isOpen, close, modal, dialogProps, showCloseButton };

  const DialogComponent: React.FC<{ children: React.ReactNode }> = useCallback(
    ({ children }) => {
      const { isOpen, close, modal, dialogProps, showCloseButton } = stateRef.current;
      return (
        <Dialog
          open={isOpen}
          onClose={(_event, reason) => {
            if (modal && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
              return;
            }
            close();
          }}
          disableEscapeKeyDown={modal}
          {...dialogProps}
        >
          <Box sx={{ position: 'relative' }}>
            {showCloseButton && (
              <Button
                autoFocus
                onClick={close}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 15,
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'error.dark',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s',
                  boxShadow: 1,
                  zIndex: 2
                }}
              >
                ×
              </Button>
            )}
            <Box sx={{ maxHeight: '80vh', overflow: 'auto', p: 2 }}>
              {children}
            </Box>
          </Box>
        </Dialog>
      );
    },
    [] // référence stable — lit depuis stateRef.current à chaque rendu
  );

  return [DialogComponent, open, close, isOpen];
};